import {
  Analytics,
  getAnalytics,
  isSupported,
  logEvent,
  setDefaultEventParameters,
  setUserId,
  setUserProperties,
} from "firebase/analytics";
import firebaseApp from "../firebase/firebaseConfig";

type AnalyticsPrimitive = string | number | boolean | null;
type AnalyticsParams = Record<string, unknown>;

export interface PurchaseAnalyticsParams {
  transactionId?: string;
  value?: number;
  currency?: string;
  productId?: string;
  itemName?: string;
  entryScreen?: string;
  [key: string]: unknown;
}

export class AnalyticsService {
  private static instance: AnalyticsService;
  private currentScreenName: string | null = null;
  private analyticsInstancePromise: Promise<Analytics | null> | null = null;

  private constructor() {}

  public static getInstance(): AnalyticsService {
    if (!AnalyticsService.instance) {
      AnalyticsService.instance = new AnalyticsService();
    }
    return AnalyticsService.instance;
  }

  public getCurrentScreenName(): string | null {
    return this.currentScreenName;
  }

  public async setUserId(userId: string | null): Promise<void> {
    const analytics = await this.getAnalyticsInstance();
    if (!analytics) {
      return;
    }
    setUserId(analytics, userId);
  }

  /**
   * Sets a Firebase user property — segments every subsequent event for this
   * user, not just the ones logged around the call. GA4 caps property names at
   * 24 characters and values at 36; longer values are truncated rather than
   * dropped so a slightly-too-long id still segments consistently.
   * Passing null clears the property.
   */
  public async setUserProperty(
    name: string,
    value: string | number | boolean | null,
  ): Promise<void> {
    const normalizedName = this.normalizeEventName(name).slice(0, 24);
    if (!normalizedName) {
      return;
    }
    const normalizedValue =
      value === null || value === undefined ? null : String(value).slice(0, 36);
    const analytics = await this.getAnalyticsInstance();
    if (!analytics) {
      return;
    }
    setUserProperties(analytics, { [normalizedName]: normalizedValue });
  }

  /**
   * Parameters attached to every subsequent event (experiment variants etc.).
   * A null value removes that parameter; passing null clears them all.
   */
  public async setDefaultEventParameters(
    params: AnalyticsParams | null,
  ): Promise<void> {
    const analytics = await this.getAnalyticsInstance();
    if (!analytics) {
      return;
    }
    if (params === null) {
      setDefaultEventParameters({});
      return;
    }
    const payload: Record<string, unknown> = {};
    Object.entries(params).forEach(([key, value]) => {
      if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(key)) {
        return;
      }
      payload[key] = value === undefined ? null : this.sanitizeValue(value, 1);
    });
    setDefaultEventParameters(payload);
  }

  public async logScreenView(
    screenName: string,
    screenClass?: string,
    extra?: AnalyticsParams,
  ): Promise<void> {
    this.currentScreenName = screenName;
    const analytics = await this.getAnalyticsInstance();
    if (!analytics) {
      return;
    }

    const sanitized = this.sanitizeParams(extra);
    logEvent(analytics, "screen_view", {
      firebase_screen: screenName,
      firebase_screen_class: screenClass ?? screenName,
      ...sanitized,
    });
  }

  public async logPurchase(params: PurchaseAnalyticsParams): Promise<void> {
    const analytics = await this.getAnalyticsInstance();
    if (!analytics) {
      return;
    }

    const {
      transactionId,
      value,
      currency,
      productId,
      itemName,
      entryScreen,
      ...rest
    } = params;

    const payload: Record<string, unknown> = {
      ...this.sanitizeParams(rest),
    };

    if (transactionId) {
      payload.transaction_id = transactionId;
    }
    if (typeof value === "number" && Number.isFinite(value) && currency) {
      payload.value = value;
      payload.currency = currency.toUpperCase();
    }
    if (productId || itemName) {
      payload.items = [
        this.sanitizeParams({
          item_id: productId,
          item_name: itemName ?? productId,
        }),
      ];
    }
    if (entryScreen) {
      payload.entry_screen = entryScreen;
    }

    // Widen to the generic string overload: the payload carries custom params
    // (e.g. entry_screen) that the typed 'purchase' overload does not accept.
    logEvent(analytics, "purchase" as string, payload);
  }

  public async logEvent(
    eventName: string,
    params?: AnalyticsParams,
  ): Promise<void> {
    const analytics = await this.getAnalyticsInstance();
    if (!analytics) {
      return;
    }
    const normalizedName = this.normalizeEventName(eventName);
    const sanitized = this.sanitizeParams(params);
    logEvent(analytics, normalizedName, sanitized);
  }

  private getAnalyticsInstance(): Promise<Analytics | null> {
    if (!this.analyticsInstancePromise) {
      this.analyticsInstancePromise = this.createAnalyticsInstance();
    }
    return this.analyticsInstancePromise;
  }

  private async createAnalyticsInstance(): Promise<Analytics | null> {
    if (typeof window === "undefined") {
      return null;
    }
    if (!firebaseApp.options.measurementId) {
      return null;
    }

    const supported = await isSupported().catch(() => false);
    if (!supported) {
      return null;
    }

    try {
      return getAnalytics(firebaseApp);
    } catch {
      return null;
    }
  }

  private normalizeEventName(eventName: string): string {
    const normalized = eventName
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_]/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_+|_+$/g, "");

    const withLeadingLetter = /^[a-z]/.test(normalized)
      ? normalized
      : `a_${normalized || "event"}`;
    return withLeadingLetter.slice(0, 40);
  }

  private sanitizeParams(
    params?: AnalyticsParams,
    depth: number = 0,
  ): Record<string, unknown> {
    if (!params || typeof params !== "object") {
      return {};
    }

    if (depth > 4) {
      return {};
    }

    const result: Record<string, unknown> = {};
    Object.entries(params).forEach(([key, value]) => {
      if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(key)) {
        return;
      }
      const sanitizedValue = this.sanitizeValue(value, depth + 1);
      if (sanitizedValue !== undefined) {
        result[key] = sanitizedValue;
      }
    });
    return result;
  }

  private sanitizeValue(value: unknown, depth: number): unknown | undefined {
    if (
      value === null ||
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean"
    ) {
      return value as AnalyticsPrimitive;
    }

    if (typeof value === "bigint") {
      return String(value);
    }

    if (
      value === undefined ||
      typeof value === "function" ||
      typeof value === "symbol"
    ) {
      return undefined;
    }

    if (Array.isArray(value)) {
      return value
        .map((item) => this.sanitizeValue(item, depth + 1))
        .filter((item) => item !== undefined)
        .slice(0, 20);
    }

    if (typeof value === "object") {
      return this.sanitizeParams(value as AnalyticsParams, depth + 1);
    }

    return undefined;
  }
}
